import useContextStore from 'src/store/useContextStore'
import useURLStore from 'src/store/useURLStore'
import type { CommonContextStore } from 'src/types/context'
import tDynamic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tDynamic'
import type {
  Lpu,
  LpuMedal,
  LpuMedalImage,
  LpuMedalType,
} from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_types'
import type { LpuMedalItem } from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuMedals/types'

const STAGE_MEDIA_BASE = 'https://stage.prodoctorov.com/media/'

/**
 * Собирает карточки медалей клиники и врачей для слайдера.
 *
 * Django отдаёт FileField и FK то строкой, то объектом с `__str__`.
 * На тестовых окружениях файлы читаются со stage — локальный `/media/` их не отдаёт.
 */
export default function mapLpuMedals(lpu: Lpu): LpuMedalItem[] {
  const { context }: CommonContextStore = useContextStore()
  const { reverse } = useURLStore()
  const mediaBase = getMediaBase(context)
  const medalsInfoPath = reverse({ name: 'info_medals' })
    || reverse({ name: 'info_pd:info_medals' })
    || ''

  return [
    ...(lpu.get_active_medals ?? []).map((medal, index) => (
      mapMedal(medal, 'clinic', index, mediaBase, medalsInfoPath)
    )),
    ...(lpu.get_all_doctors_active_medals ?? []).map((medal, index) => (
      mapMedal(medal, 'doctor', index, mediaBase, medalsInfoPath)
    )),
  ].filter((item): item is LpuMedalItem => Boolean(item))
}

function getMediaBase(context: CommonContextStore['context']) {
  if (context.global?.env?.isProd) {
    const media = context.global.static?.media || ''

    return media.endsWith('/') ? media : `${media}/`
  }

  return STAGE_MEDIA_BASE
}

function getImagePath(image: LpuMedalImage | undefined) {
  if (!image) {
    return ''
  }

  if (typeof image === 'string') {
    return image
  }

  return image.url || image.__str__ || image.name || '' // eslint-disable-line no-underscore-dangle
}

function resolveMediaUrl(path: string, mediaBase: string) {
  if (!path) {
    return ''
  }

  if (/^https?:\/\//.test(path) || path.startsWith('//')) {
    return path
  }

  const relativePath = path.replace(/^\/+/, '').replace(/^media\//, '')

  return new URL(relativePath, mediaBase).href
}

function getGroupId(group: LpuMedalType['medal_group']) {
  if (group == null) {
    return undefined
  }

  const rawId = typeof group === 'object'
    ? group.id ?? group.__str__ // eslint-disable-line no-underscore-dangle
    : group
  const groupId = Number(rawId)

  return Number.isNaN(groupId) ? undefined : groupId
}

function getMedalType(raw: LpuMedal): LpuMedalType {
  if (raw.medal_type && typeof raw.medal_type === 'object') {
    return raw.medal_type
  }

  return raw
}

function getMedalName(raw: LpuMedal, medalType: LpuMedalType, source: 'clinic' | 'doctor') {
  const preferredField = source === 'doctor' ? 'name_in_lpu' : 'name'

  return tDynamic(medalType, preferredField)
    || tDynamic(raw, preferredField)
    || tDynamic(medalType, 'name')
    || tDynamic(raw, 'name')
}

function mapMedal(
  raw: LpuMedal,
  source: 'clinic' | 'doctor',
  index: number,
  mediaBase: string,
  medalsInfoPath: string,
): LpuMedalItem | null {
  const medalType = getMedalType(raw)
  const src = resolveMediaUrl(
    getImagePath(medalType.image || raw.image),
    mediaBase,
  )

  if (!src) {
    return null
  }

  const typeId = medalType.id ?? raw.id ?? index
  const groupId = getGroupId(medalType.medal_group ?? raw.medal_group)
  const hash = groupId
    ? `#medalgroup-${groupId}`
    : `#medaltype-${typeId}`

  return {
    id: `${source}-${typeId}`,
    src,
    alt: getMedalName(raw, medalType, source),
    href: `${medalsInfoPath}${hash}`,
    count: raw.total || 1,
  }
}
